import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";

export default function RecordingsPlaylistModal({ isOpen, onClose, lessonTitle, recordings }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const videoRef = useRef(null);

  const safeRecordings = Array.isArray(recordings)
    ? recordings.filter((r) => r?.recordingUrl)
    : [];
  const currentRecording = safeRecordings[currentIndex] || null;

  useEffect(() => {
    if (!isOpen) return;
    setCurrentIndex(0);
  }, [isOpen, lessonTitle]);

  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen || !videoRef.current) return;
    videoRef.current.load();
  }, [isOpen, currentIndex]);

  const handleEnded = () => {
    if (currentIndex < safeRecordings.length - 1)
      setCurrentIndex((i) => i + 1);
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[1000] flex items-end sm:items-center justify-center px-0 sm:px-4"
      aria-modal="true"
      role="dialog"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-gray-950/60 backdrop-blur-[3px]"
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className="relative z-[1] w-full sm:max-w-5xl bg-white
                   rounded-t-3xl sm:rounded-3xl shadow-2xl shadow-black/20
                   flex flex-col max-h-[96dvh] sm:max-h-[90vh] overflow-hidden"
        dir="rtl"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative flex-shrink-0 bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-gray-200 px-5 sm:px-7 py-5">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 shadow-md flex items-center justify-center">
              <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
                <path d="M10 8.5l5 3.5-5 3.5V8.5z" fill="currentColor" />
              </svg>
            </div>
            <div className="min-w-0">
              <h2 className="text-gray-800 font-bold text-base sm:text-lg leading-tight truncate">
                {lessonTitle || "הקלטות שיעור"}
              </h2>
              <p className="text-gray-500 text-xs mt-0.5">
                {safeRecordings.length > 1
                  ? `מתנגן ברצף: הקלטה ${currentIndex + 1} מתוך ${safeRecordings.length}`
                  : "הקלטת שיעור"}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-8 h-8 rounded-xl bg-white/80 hover:bg-white border border-gray-200 hover:border-gray-300 flex items-center justify-center transition-colors"
            aria-label="סגור"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M2 2l10 10M12 2L2 12" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-hidden flex flex-col lg:grid lg:grid-cols-[280px_minmax(0,1fr)]">

          {/* Video — top on mobile, right on desktop */}
          <div className="order-first lg:order-last bg-black p-3 sm:p-4">
            {currentRecording ? (
              <video
                ref={videoRef}
                key={currentRecording.recordingUrl}
                controls
                autoPlay
                onEnded={handleEnded}
                className="w-full rounded-xl bg-black object-contain h-[38vh] sm:h-[45vh] lg:h-[55vh]"
              >
                <source src={currentRecording.recordingUrl} />
                הדפדפן שלך אינו תומך בניגון וידאו.
              </video>
            ) : (
              <div className="flex items-center justify-center rounded-xl bg-gray-900 text-sm text-gray-300 h-[38vh] sm:h-[45vh] lg:h-[55vh]">
                אין הקלטות זמינות
              </div>
            )}
          </div>

          {/* Sidebar — below video on mobile, left on desktop */}
          <aside className="order-last lg:order-first overflow-y-auto max-h-[30vh] lg:max-h-none border-t border-gray-200 bg-gray-50 lg:border-t-0 lg:border-l lg:border-gray-200">
            <div className="p-3">
              <div className="mb-2 text-sm font-semibold text-gray-700">רשימת הקלטות</div>
              <div className="space-y-2">
                {safeRecordings.map((rec, idx) => {
                  const isActive = idx === currentIndex;
                  return (
                    <button
                      key={rec.id || rec.zoomRecordingId || idx}
                      type="button"
                      onClick={() => setCurrentIndex(idx)}
                      className={[
                        "w-full rounded-xl border px-3 py-3 text-right transition",
                        isActive
                          ? "border-indigo-300 bg-indigo-50 text-indigo-700"
                          : "border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50",
                      ].join(" ")}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-sm font-semibold">הקלטה {idx + 1}</span>
                        {isActive && (
                          <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-[11px] font-bold text-indigo-700">
                            מתנגן
                          </span>
                        )}
                      </div>
                      <div className="mt-1 text-xs text-gray-500">
                        {rec.recordingType || rec.fileType || "וידאו"}
                      </div>
                      {rec.recordingStart && (
                        <div className="mt-1 text-[11px] text-gray-400">
                          {new Date(rec.recordingStart).toLocaleString("he-IL")}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>,
    document.body
  );
}